import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, Toaster } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Plus, Trash2, Edit2, Upload, RefreshCw, Save, Video,
  GraduationCap, Eye, Link2, DollarSign, X, CheckCircle2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminCourses() {
  const navigate = useNavigate();
  const [token] = useState(localStorage.getItem('adminToken') || '');
  const [loading, setLoading] = useState(true);
  const [parts, setParts] = useState([]);
  const [bundle, setBundle] = useState(null);
  const [editingPart, setEditingPart] = useState(null); // {slug, price, payment_link, ...}
  const [editingBundle, setEditingBundle] = useState(false);
  const [bundleForm, setBundleForm] = useState({ price: '', payment_link: '' });
  const [activePartSlug, setActivePartSlug] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [showNewLesson, setShowNewLesson] = useState(false);
  const [newLessonTitle, setNewLessonTitle] = useState('');
  const [newLessonDesc, setNewLessonDesc] = useState('');
  const [newLessonConcepts, setNewLessonConcepts] = useState('');
  const [newLessonFreePreview, setNewLessonFreePreview] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerLessonId, setPickerLessonId] = useState(null);
  const [bunnyVideos, setBunnyVideos] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [manualVideoId, setManualVideoId] = useState('');

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    loadParts();
    // eslint-disable-next-line
  }, []);

  const loadParts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/elearning/admin/parts?token=${token}`);
      const json = await res.json();
      if (json.success) {
        setParts(json.parts);
        setBundle(json.bundle);
        setBundleForm({ price: json.bundle?.price ?? '', payment_link: json.bundle?.payment_link ?? '' });
      } else toast.error('Auth failed — re-login');
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  };

  const openPart = async (slug) => {
    setActivePartSlug(slug);
    try {
      const res = await fetch(`${API}/elearning/admin/part/${slug}/lessons?token=${token}`);
      const json = await res.json();
      if (json.success) setLessons(json.lessons);
    } catch (e) { toast.error('Load lessons failed'); }
  };

  const savePart = async () => {
    if (!editingPart) return;
    try {
      const res = await fetch(`${API}/elearning/admin/part/${editingPart.slug}?token=${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editingPart.title,
          tagline: editingPart.tagline,
          price: editingPart.price === '' ? null : parseFloat(editingPart.price),
          payment_link: editingPart.payment_link,
        }),
      });
      const json = await res.json();
      if (json.success) {
        toast.success('Saved');
        setEditingPart(null);
        loadParts();
      }
    } catch (e) { toast.error('Save failed'); }
  };

  const saveBundle = async () => {
    try {
      const res = await fetch(`${API}/elearning/admin/bundle?token=${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          price: bundleForm.price === '' ? null : parseFloat(bundleForm.price),
          payment_link: bundleForm.payment_link,
        }),
      });
      const json = await res.json();
      if (json.success) { toast.success('Bundle saved'); setEditingBundle(false); loadParts(); }
    } catch (e) { toast.error('Save failed'); }
  };

  const addLesson = async () => {
    if (!newLessonTitle.trim()) { toast.error('Title required'); return; }
    const part = parts.find(p => p.slug === activePartSlug);
    try {
      const res = await fetch(`${API}/elearning/admin/lesson?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          part_id: part.id,
          title: newLessonTitle.trim(),
          description: newLessonDesc,
          concepts: newLessonConcepts.split(',').map(s => s.trim()).filter(Boolean),
          free_preview: newLessonFreePreview,
        }),
      });
      const json = await res.json();
      if (json.success) {
        toast.success('Lesson added');
        setNewLessonTitle(''); setNewLessonDesc(''); setNewLessonConcepts(''); setNewLessonFreePreview(false);
        setShowNewLesson(false);
        openPart(activePartSlug);
        loadParts();
      }
    } catch (e) { toast.error('Add failed'); }
  };

  const deleteLesson = async (id) => {
    if (!window.confirm('Delete this lesson? This will also delete its video from Bunny.net.')) return;
    try {
      const res = await fetch(`${API}/elearning/admin/lesson/${id}?token=${token}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) { toast.success('Deleted'); openPart(activePartSlug); loadParts(); }
    } catch (e) { toast.error('Delete failed'); }
  };

  const uploadVideo = async (lessonId, file) => {
    if (!file) return;
    setUploadingId(lessonId);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/elearning/admin/lesson/${lessonId}/upload?token=${token}`, {
        method: 'POST',
        body: fd,
      });
      const json = await res.json();
      if (json.success) {
        toast.success('Uploaded · Bunny is now processing');
        openPart(activePartSlug);
      } else toast.error(json.detail || 'Upload failed');
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploadingId(null); }
  };

  const syncMeta = async (lessonId) => {
    try {
      const res = await fetch(`${API}/elearning/admin/lesson/${lessonId}/sync?token=${token}`, { method: 'POST' });
      const json = await res.json();
      if (json.success) { toast.success('Metadata synced'); openPart(activePartSlug); }
      else toast.error(json.message || 'Sync failed');
    } catch (e) { toast.error('Sync failed'); }
  };

  const openPicker = async (lessonId) => {
    setPickerLessonId(lessonId);
    setPickerOpen(true);
    setManualVideoId('');
    setPickerLoading(true);
    try {
      const res = await fetch(`${API}/elearning/admin/bunny/videos?token=${token}`);
      const json = await res.json();
      if (json.success) setBunnyVideos(json.videos || []);
      else toast.error('Could not list Bunny videos');
    } catch (e) { toast.error('Bunny request failed'); }
    finally { setPickerLoading(false); }
  };

  const attachBunnyVideo = async (videoId) => {
    if (!videoId) return;
    try {
      const res = await fetch(`${API}/elearning/admin/lesson/${pickerLessonId}/attach?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bunny_video_id: videoId }),
      });
      const json = await res.json();
      if (json.success) {
        toast.success(`Linked · status: ${json.status}`);
        setPickerOpen(false);
        openPart(activePartSlug);
        loadParts();
      } else {
        toast.error(json.detail || 'Attach failed');
      }
    } catch (e) { toast.error('Attach failed'); }
  };

  const fmtDur = (sec) => {
    if (!sec) return '';
    const m = Math.floor(sec / 60);
    const s = String(sec % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Toaster position="top-right" richColors />
      <header className="sticky top-0 z-30 bg-black/95 backdrop-blur-md border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <button onClick={() => navigate('/admin/dashboard')} className="flex items-center gap-2 text-amber-300 hover:text-amber-200 text-sm" data-testid="admin-courses-back">
            <ArrowLeft className="w-4 h-4" /> Admin
          </button>
          <div className="text-center">
            <h1 className="text-sm sm:text-base font-bold flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-amber-300" /> Course Manager
            </h1>
          </div>
          <Button size="sm" variant="outline" onClick={loadParts} className="border-amber-500/30 text-amber-300 text-xs" data-testid="admin-courses-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {loading && <p className="text-center text-gray-500">Loading…</p>}

        {/* Bundle config */}
        {bundle && (
          <Card className="bg-zinc-950 border border-amber-500/30">
            <CardContent className="p-5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="font-bold text-amber-300">Bundle — All 3 Parts</h3>
                  <p className="text-xs text-gray-400">Set bundle price and Square payment link.</p>
                </div>
                {!editingBundle ? (
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-lg font-bold">${bundle.price?.toFixed(0)}</div>
                      <div className="text-[10px] text-gray-500">
                        {bundle.payment_link ? '✓ Payment link set' : '⚠ No payment link'}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setEditingBundle(true)} className="border-amber-500/30 text-amber-300" data-testid="bundle-edit">
                      <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Input type="number" value={bundleForm.price} onChange={e => setBundleForm({ ...bundleForm, price: e.target.value })} placeholder="Price" className="w-24 bg-zinc-900 border-amber-500/20 text-sm" data-testid="bundle-price-input" />
                    <Input value={bundleForm.payment_link} onChange={e => setBundleForm({ ...bundleForm, payment_link: e.target.value })} placeholder="Square payment link" className="w-72 bg-zinc-900 border-amber-500/20 text-sm" data-testid="bundle-link-input" />
                    <Button size="sm" onClick={saveBundle} className="bg-amber-500 text-black" data-testid="bundle-save"><Save className="w-3.5 h-3.5 mr-1" /> Save</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingBundle(false)} className="border-zinc-700">Cancel</Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Parts grid */}
        <div className="grid sm:grid-cols-3 gap-4">
          {parts.map(p => (
            <Card key={p.id} className="bg-zinc-950 border border-amber-500/20" data-testid={`admin-part-${p.slug}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Badge className="bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[9px]">Part {p.order}</Badge>
                  <Badge variant="outline" className="border-zinc-700 text-gray-400 text-[9px]">{p.lessons_count || 0} lessons</Badge>
                </div>
                <h3 className="font-bold text-sm leading-tight mb-1">{p.title}</h3>
                <p className="text-[11px] text-gray-500 line-clamp-2 mb-3">{p.tagline}</p>

                {editingPart?.slug !== p.slug ? (
                  <>
                    <div className="text-lg font-bold mb-1">${p.price?.toFixed(0)}</div>
                    <div className="text-[10px] text-gray-500 mb-3">
                      {p.payment_link ? '✓ Payment link set' : '⚠ No payment link'}
                    </div>
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => setEditingPart({ ...p })} className="border-amber-500/30 text-amber-300 text-xs flex-1" data-testid={`admin-part-${p.slug}-edit`}>
                        <Edit2 className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button size="sm" onClick={() => openPart(p.slug)} className="bg-amber-500 text-black text-xs flex-1" data-testid={`admin-part-${p.slug}-lessons`}>
                        Lessons <Video className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    <Input value={editingPart.title} onChange={e => setEditingPart({ ...editingPart, title: e.target.value })} placeholder="Title" className="bg-zinc-900 border-amber-500/20 text-xs" />
                    <Input value={editingPart.tagline} onChange={e => setEditingPart({ ...editingPart, tagline: e.target.value })} placeholder="Tagline" className="bg-zinc-900 border-amber-500/20 text-xs" />
                    <Input type="number" value={editingPart.price} onChange={e => setEditingPart({ ...editingPart, price: e.target.value })} placeholder="Price" className="bg-zinc-900 border-amber-500/20 text-xs" />
                    <Input value={editingPart.payment_link || ''} onChange={e => setEditingPart({ ...editingPart, payment_link: e.target.value })} placeholder="Square payment link" className="bg-zinc-900 border-amber-500/20 text-xs" />
                    <div className="flex gap-1.5">
                      <Button size="sm" onClick={savePart} className="bg-emerald-500 text-black text-xs flex-1"><Save className="w-3 h-3 mr-1" /> Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingPart(null)} className="border-zinc-700 text-xs flex-1">Cancel</Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Lessons editor */}
        {activePartSlug && (
          <Card className="bg-zinc-950 border border-amber-500/30">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="font-bold text-amber-300">
                  Lessons — {parts.find(p => p.slug === activePartSlug)?.title}
                </h3>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setActivePartSlug(null)} className="border-zinc-700 text-xs">Close</Button>
                  <Button size="sm" onClick={() => setShowNewLesson(true)} className="bg-amber-500 text-black text-xs" data-testid="admin-lesson-new">
                    <Plus className="w-3 h-3 mr-1" /> New lesson
                  </Button>
                </div>
              </div>

              {showNewLesson && (
                <div className="mb-4 rounded-lg p-3 bg-zinc-900 border border-amber-500/20 space-y-2">
                  <Input value={newLessonTitle} onChange={e => setNewLessonTitle(e.target.value)} placeholder="Lesson title" className="bg-zinc-950 border-amber-500/20 text-sm" data-testid="admin-lesson-title-input" />
                  <Input value={newLessonDesc} onChange={e => setNewLessonDesc(e.target.value)} placeholder="Short description (optional)" className="bg-zinc-950 border-amber-500/20 text-sm" />
                  <Input value={newLessonConcepts} onChange={e => setNewLessonConcepts(e.target.value)} placeholder="Concepts tag list (comma-separated): fvg, liquidity_grab, …" className="bg-zinc-950 border-amber-500/20 text-sm" />
                  <label className="flex items-center gap-2 text-xs text-gray-300">
                    <input type="checkbox" checked={newLessonFreePreview} onChange={e => setNewLessonFreePreview(e.target.checked)} data-testid="admin-lesson-preview-cb" />
                    Mark as free preview (anyone signed in can watch)
                  </label>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={addLesson} className="bg-emerald-500 text-black text-xs" data-testid="admin-lesson-create">Create</Button>
                    <Button size="sm" variant="outline" onClick={() => setShowNewLesson(false)} className="border-zinc-700 text-xs">Cancel</Button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {lessons.length === 0 && <p className="text-xs text-gray-500 text-center py-6">No lessons yet. Add one to get started.</p>}
                {lessons.map(l => (
                  <div key={l.id} className="rounded-lg p-3 bg-zinc-900 border border-zinc-800 flex items-center gap-3 flex-wrap" data-testid={`admin-lesson-row-${l.id}`}>
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-amber-300 text-xs">#{l.order}</span>
                        <span className="font-bold text-sm">{l.title}</span>
                        {l.free_preview && <Badge className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[9px]">Free</Badge>}
                        {!l.bunny_video_id && <Badge variant="outline" className="border-rose-500/30 text-rose-300 text-[9px]">No video</Badge>}
                        {l.bunny_video_id && l.video_status === 'processing' && <Badge variant="outline" className="border-amber-500/30 text-amber-300 text-[9px]">Processing</Badge>}
                        {l.bunny_video_id && l.video_status === 'ready' && <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 text-[9px]">Ready · {l.duration_sec ? Math.floor(l.duration_sec / 60) + 'm' : ''}</Badge>}
                      </div>
                      {l.description && <p className="text-[11px] text-gray-500 mt-0.5">{l.description}</p>}
                      {l.concepts?.length > 0 && (
                        <p className="text-[9px] text-amber-300/60 mt-0.5 uppercase tracking-widest">
                          {l.concepts.join(' · ')}
                        </p>
                      )}
                    </div>
                    <label className="cursor-pointer">
                      <input type="file" accept="video/*" hidden onChange={e => uploadVideo(l.id, e.target.files[0])} data-testid={`admin-lesson-upload-${l.id}`} />
                      <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold border ${uploadingId === l.id ? 'bg-zinc-800 text-gray-500 cursor-wait' : 'bg-amber-500 text-black hover:bg-amber-400'}`}>
                        <Upload className="w-3 h-3" /> {uploadingId === l.id ? 'Uploading…' : l.bunny_video_id ? 'Replace' : 'Upload'}
                      </span>
                    </label>
                    <Button size="sm" variant="outline" onClick={() => openPicker(l.id)} className="border-emerald-500/30 text-emerald-300 text-xs" data-testid={`admin-lesson-pick-${l.id}`} title="Pick a video you've already uploaded to Bunny.net">
                      <Link2 className="w-3 h-3 mr-1" /> Pick from Bunny
                    </Button>
                    {l.bunny_video_id && (
                      <Button size="sm" variant="outline" onClick={() => syncMeta(l.id)} className="border-amber-500/30 text-amber-300 text-xs" data-testid={`admin-lesson-sync-${l.id}`}>
                        <RefreshCw className="w-3 h-3 mr-1" /> Sync
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => deleteLesson(l.id)} className="border-rose-500/30 text-rose-400 text-xs" data-testid={`admin-lesson-delete-${l.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </section>

      {/* Bunny.net video picker modal */}
      {pickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setPickerOpen(false)}>
          <div className="w-full max-w-3xl max-h-[85vh] bg-zinc-950 rounded-2xl border-2 border-emerald-500/30 flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-base flex items-center gap-2 text-emerald-300">
                  <Link2 className="w-4 h-4" /> Pick a video from your Bunny.net library
                </h3>
                <p className="text-[11px] text-gray-500">Videos uploaded directly on Bunny.net. Click one to link it to this lesson.</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setPickerOpen(false)} className="border-zinc-700"><X className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="px-5 py-3 bg-zinc-900 border-b border-zinc-800">
              <Label className="text-[10px] uppercase tracking-widest text-gray-400">Or paste a video GUID / embed URL</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  value={manualVideoId}
                  onChange={e => setManualVideoId(e.target.value)}
                  placeholder="e.g. 5fab1234-...  or  https://iframe.mediadelivery.net/embed/671191/<guid>"
                  className="bg-zinc-950 border-zinc-800 text-xs flex-1"
                  data-testid="bunny-picker-manual-input"
                />
                <Button size="sm" onClick={() => attachBunnyVideo(manualVideoId.trim())} disabled={!manualVideoId.trim()} className="bg-emerald-500 text-black text-xs disabled:bg-zinc-800 disabled:text-gray-500" data-testid="bunny-picker-attach-manual">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Link
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              {pickerLoading && <p className="text-center text-gray-500 text-sm py-8">Loading Bunny library…</p>}
              {!pickerLoading && bunnyVideos.length === 0 && (
                <p className="text-center text-gray-500 text-sm py-8">
                  No videos found in your Bunny.net library yet. Upload one to bunny.net first, or use the file uploader on the lesson row.
                </p>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                {bunnyVideos.map(v => (
                  <button
                    key={v.guid}
                    onClick={() => attachBunnyVideo(v.guid)}
                    className="text-left rounded-lg bg-zinc-900 border border-zinc-800 hover:border-emerald-500/40 transition-colors overflow-hidden"
                    data-testid={`bunny-picker-video-${v.guid}`}
                  >
                    {v.thumbnail_url ? (
                      <img src={v.thumbnail_url} alt={v.title} className="w-full aspect-video object-cover bg-black" loading="lazy" />
                    ) : (
                      <div className="w-full aspect-video bg-zinc-800 flex items-center justify-center text-zinc-600 text-xs">
                        <Video className="w-6 h-6" />
                      </div>
                    )}
                    <div className="p-3">
                      <h4 className="font-bold text-sm text-white truncate">{v.title || '(untitled)'}</h4>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500">
                        {v.status === 4 ? <span className="text-emerald-400">● Ready</span> : <span className="text-amber-400">● Processing</span>}
                        {v.length > 0 && <span className="font-mono">{fmtDur(v.length)}</span>}
                      </div>
                      <p className="text-[9px] text-gray-600 mt-1 font-mono truncate">{v.guid}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
